/**
 * @file: useKeyboardInset.ts
 *
 * A single, self-managed keyboard inset for the composer. It animates with
 * the OS keyboard's OWN duration/easing (via native LayoutAnimation), so
 * the motion is OS-controlled and 60fps.
 *
 * While FROZEN, every keyboard frame event is ignored and the inset is
 * pinned — so the composer does NOT move from the moment the mention
 * picker opens until the keyboard has settled after re-focus. This
 * sidesteps iOS's transient mid-animation frames (predictive bar resizing)
 * that caused the dip/bounce.
 *
 * iOS only: Android resizes the window itself, so the inset stays at 0.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Dimensions, Keyboard, LayoutAnimation, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Timer = ReturnType<typeof setTimeout>;

export interface KeyboardInset {
  /** Current bottom inset (px) the composer strip should pad by. */
  inset: number;
  /** Freeze the inset at its current value (no-op if the keyboard is down). */
  freeze: () => void;
  /** Unfreeze and collapse the inset to 0 (keyboard won't return). */
  unfreeze: () => void;
  /**
   * Unfreeze after the keyboard has settled (~900ms) and re-sync to its
   * real height — used when re-focus is expected (mention picked).
   */
  scheduleResync: () => void;
}

export function useKeyboardInset(): KeyboardInset {
  const safeAreaInsets = useSafeAreaInsets();
  const insetRef = useRef(0);
  const frozenRef = useRef(false);
  const unfreezeTimerRef = useRef<Timer | null>(null);
  const [inset, setInset] = useState(0);

  // Set the inset, animating with the OS keyboard's own duration/easing via
  // native LayoutAnimation (same call KeyboardAvoidingView makes internally
  // → OS-controlled, 60fps). No-ops when the value is unchanged.
  const applyInset = useCallback(
    (value: number, duration?: number, easing?: string) => {
      if (insetRef.current === value) return;
      insetRef.current = value;
      const d = duration && duration > 0 ? duration : 250;
      const type =
        (easing && (LayoutAnimation.Types as any)[easing]) ||
        LayoutAnimation.Types.keyboard;
      LayoutAnimation.configureNext({
        duration: d,
        update: { duration: d, type },
      });
      setInset(value);
    },
    [],
  );

  // Read the keyboard's settled inset (iOS ≥ 0.71): `height − bottom safe
  // area` (the input bar already pads the home strip). Returns null when
  // `Keyboard.metrics` is unavailable, so callers can leave the inset as-is
  // rather than wrongly collapsing the composer.
  const currentInset = useCallback((): number | null => {
    const m = Keyboard.metrics?.();
    if (!m) return null;
    return m.height > 0 ? Math.max(0, m.height - safeAreaInsets.bottom) : 0;
  }, [safeAreaInsets.bottom]);

  // iOS: own the keyboard inset (animate paddingBottom natively). While
  // FROZEN (picker open → keyboard settled after re-focus) every frame is
  // ignored, so the composer stays put no matter how the keyboard animates.
  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    const sub = Keyboard.addListener('keyboardWillChangeFrame', e => {
      if (frozenRef.current) return; // composer frozen during the picker
      const screenH = Dimensions.get('window').height;
      const endY = e.endCoordinates?.screenY ?? screenH;
      const h = e.endCoordinates?.height ?? 0;
      const visible = h > 0 && endY < screenH - 1;
      const target = visible ? Math.max(0, h - safeAreaInsets.bottom) : 0;
      applyInset(target, e.duration, e.easing);
    });
    return () => sub.remove();
  }, [applyInset, safeAreaInsets.bottom]);

  // Clean up the unfreeze timer on unmount.
  useEffect(
    () => () => {
      if (unfreezeTimerRef.current) clearTimeout(unfreezeTimerRef.current);
    },
    [],
  );

  const freeze = useCallback(() => {
    // Freeze only when the keyboard is actually up — otherwise blurring for
    // the picker would have nothing to pin.
    if (insetRef.current > 0) frozenRef.current = true;
  }, []);

  const unfreeze = useCallback(() => {
    if (unfreezeTimerRef.current) clearTimeout(unfreezeTimerRef.current);
    frozenRef.current = false;
    applyInset(0);
  }, [applyInset]);

  const scheduleResync = useCallback(() => {
    if (unfreezeTimerRef.current) clearTimeout(unfreezeTimerRef.current);
    unfreezeTimerRef.current = setTimeout(() => {
      frozenRef.current = false;
      // Sync to the keyboard's settled height (no-op if it matches the
      // frozen value). Leave untouched if metrics are unavailable.
      const settled = currentInset();
      if (settled != null) applyInset(settled);
    }, 900);
  }, [applyInset, currentInset]);

  return { inset, freeze, unfreeze, scheduleResync };
}
